// Campo Avaliação — o mais sensível (Regras 1 e 7). Nunca é preenchido pela IA:
// vem vazio de propósito e SÓ o médico digita. Fica SEMPRE em âmbar (mesmo depois
// de preenchido) para lembrar que é ato médico e exige revisão — nunca silencioso.
// Sem atalho de inserção, sem sugestão do modelo: campo vazio + digitação manual.

import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { cor, fonte } from '../../styles/tokens';
import { CopiarBotao } from './CopiarBotao';

const moldura: CSSProperties = {
  border: `2px solid ${cor.ambarBorda}`,
  background: cor.ambarBg,
  display: 'flex',
  flexDirection: 'column',
};

const cabecalho: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 12px',
  borderBottom: `1px solid rgba(168,106,0,.35)`,
};

const rotuloDireita: CSSProperties = {
  marginLeft: 'auto',
  fontFamily: fonte.mono,
  fontSize: 10,
  color: cor.ambarBorda,
  letterSpacing: '.1em',
};

const copiarAmbar: CSSProperties = { borderColor: cor.ambarBorda, color: cor.ambarTexto };

interface Props {
  valor: string;
  onChange: (valor: string) => void;
}

export function CampoAvaliacao({ valor, onChange }: Props): JSX.Element {
  const [editando, setEditando] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const preenchida = valor.trim() !== '';

  useEffect(() => {
    if (editando) {
      const ta = taRef.current;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(ta.value.length, ta.value.length);
      }
    }
  }, [editando]);

  return (
    <div style={moldura}>
      <div style={cabecalho}>
        <span style={{ fontFamily: fonte.mono, fontSize: 12, color: cor.ambarBorda }}>A</span>
        <span
          style={{
            fontFamily: fonte.condensada,
            fontWeight: 600,
            fontSize: 16,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: cor.ambarTexto,
          }}
        >
          Avaliação
        </span>
        <span style={rotuloDireita}>
          {preenchida ? 'PREENCHIDA POR VOCÊ · REVISE' : 'SOMENTE VOCÊ ESCREVE AQUI'}
        </span>
        <CopiarBotao texto={valor} rotulo="Copiar A" style={copiarAmbar} disabled={!preenchida} />
      </div>

      {editando ? (
        <div style={{ padding: '12px 14px' }}>
          <textarea
            ref={taRef}
            value={valor}
            placeholder="Digite a avaliação…"
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => setEditando(false)}
            style={{
              width: '100%',
              minHeight: 88,
              padding: '8px 10px',
              fontFamily: fonte.serif,
              fontSize: 14.5,
              lineHeight: 1.62,
              color: cor.texto,
              background: cor.branco,
              border: `1px solid ${cor.ambarBorda}`,
              outline: `2px solid ${cor.ambarBorda}`,
              outlineOffset: -2,
              resize: 'vertical',
            }}
          />
        </div>
      ) : preenchida ? (
        <PreenchidaView valor={valor} onEditar={() => setEditando(true)} />
      ) : (
        <VaziaView onEditar={() => setEditando(true)} />
      )}
    </div>
  );
}

// Estado vazio: instrução explícita de que o vazio é proposital (não é falha) +
// alvo de digitação. Nenhuma sugestão de conteúdo — o médico escreve do zero.
function VaziaView({ onEditar }: { onEditar: () => void }): JSX.Element {
  return (
    <div style={{ padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span style={{ fontFamily: fonte.mono, fontSize: 15, color: cor.ambarBorda, lineHeight: 1.3 }}>
        [?]
      </span>
      <div>
        <div
          style={{
            fontFamily: fonte.corpo,
            fontSize: 13.5,
            fontWeight: 500,
            color: cor.ambarTexto,
            lineHeight: 1.45,
          }}
        >
          Este campo vem vazio de propósito — não é falha.
        </div>
        <div style={{ fontSize: 13, color: cor.ambarTexto, lineHeight: 1.5, marginTop: 2, opacity: 0.9 }}>
          O vmed transcreve, não diagnostica. A hipótese e o CID são seu ato médico. Escreva aqui
          antes de copiar.
        </div>
        <div
          onClick={onEditar}
          title="Clique para escrever a avaliação"
          style={{
            marginTop: 10,
            minHeight: 56,
            border: `1px dashed rgba(168,106,0,.55)`,
            background: 'rgba(255,255,255,.5)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px',
            fontFamily: fonte.serif,
            fontSize: 14.5,
            color: 'rgba(122,77,0,.5)',
            cursor: 'text',
          }}
        >
          Digite a avaliação…
        </div>
      </div>
    </div>
  );
}

// Estado preenchido: texto do médico + lembrete permanente (âmbar não sai — Regra 7).
function PreenchidaView({
  valor,
  onEditar,
}: {
  valor: string;
  onEditar: () => void;
}): JSX.Element {
  return (
    <div style={{ padding: '12px 14px' }}>
      <div
        onClick={onEditar}
        title="Clique para editar"
        style={{
          fontFamily: fonte.serif,
          fontSize: 14.5,
          lineHeight: 1.62,
          color: cor.texto,
          whiteSpace: 'pre-wrap',
          cursor: 'text',
        }}
      >
        {valor}
      </div>
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          gap: 9,
          alignItems: 'flex-start',
          borderTop: `1px solid rgba(168,106,0,.35)`,
          paddingTop: 9,
        }}
      >
        <span style={{ fontFamily: fonte.mono, fontSize: 13, color: cor.ambarBorda, lineHeight: 1.3 }}>
          !
        </span>
        <div style={{ fontSize: 12.5, color: cor.ambarTexto, lineHeight: 1.5 }}>
          Avaliação escrita por você — o vmed não diagnostica. Permanece em âmbar para lembrar que
          exige sua revisão antes de copiar.
        </div>
      </div>
    </div>
  );
}
